import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  getGrowthDetail,
  type GrowthScore,
  type GrowthDetail,
} from "@/api/usGrowth";
import { growthReason, growthScoreRuleLabel } from "@/lib/usGrowth";
import { UsGrowthAssessment } from "./UsGrowthAssessment";
import { Button } from "@/components/ui/button";

export function UsGrowthScoreCard({
  score,
  heading = true,
}: {
  score: GrowthScore;
  heading?: boolean;
}) {
  return (
    <div
      className={
        heading ? "rounded-xl border border-slate-200 bg-slate-50/60 p-4" : ""
      }
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {heading && (
            <h4 className="text-sm font-semibold text-slate-900">Score CFO</h4>
          )}
          <p className="mt-1 text-xs text-slate-500">
            {score.status === "BLOCKED"
              ? "Société bloquée — revue nécessaire"
              : score.points === null
                ? "Données insuffisantes pour calculer le score"
                : score.status === "PARTIAL"
                  ? `Calcul partiel · ${score.known_rules}/${score.total_rules} critères documentés`
                  : `${score.known_rules}/${score.total_rules} critères documentés`}
          </p>
        </div>
        <div className="text-right">
          <span className="text-2xl font-semibold tabular-nums text-slate-900">
            {score.points ?? "—"}
          </span>
          {score.points !== null && (
            <span className="ml-1 text-xs text-slate-500">pts documentés</span>
          )}
        </div>
      </div>
      <details className="mt-3">
        <summary className="cursor-pointer text-xs font-medium text-indigo-600">
          Voir le calcul et les données manquantes
        </summary>
        <ul className="mt-3 divide-y divide-slate-100">
          {score.rules.map((rule) => (
            <li key={rule.key} className="py-2 text-xs">
              <div className="flex justify-between gap-3">
                <span className="text-slate-700">
                  {growthScoreRuleLabel(rule.key, rule.label)}
                </span>
                <span className="shrink-0 tabular-nums text-slate-500">
                  {rule.status === "DISABLED"
                    ? "Désactivé"
                    : rule.points === null
                      ? "À documenter"
                      : `${rule.points} / ${rule.max_points} pts`}
                </span>
              </div>
              {rule.evidence && (
                <p className="mt-1 text-slate-500">
                  {rule.evidence
                    .replace(
                      "Latest acquired funding observation",
                      "Dernière observation de financement conservée",
                    )
                    .replace(
                      "Documented historical reference round; current stage not established",
                      "Tour historique documenté ; stade actuel à confirmer",
                    )
                    .replace(
                      "Historical last-round observation",
                      "Dernier tour documenté historiquement",
                    )}{" "}
                  · {rule.source?.split(" · ")[0]}
                </p>
              )}
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-slate-500">
          Barème CFO du projet, sur {score.max_points} points. Les critères
          inconnus restent à documenter. Ce calcul historique ne valide pas la
          prospection.
        </p>
        {!!score.blockers.length && (
          <ul className="mt-2 space-y-1 text-xs text-amber-800">
            {score.blockers.map((b) => (
              <li key={b}>{growthReason(b)}</li>
            ))}
          </ul>
        )}
      </details>
    </div>
  );
}

export function UsGrowthScorePanel({
  slug,
  companyId,
  revision,
}: {
  slug: string;
  companyId: number;
  revision: number;
}) {
  const [data, setData] = useState<GrowthDetail | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoaded(false);
    setError(false);
    getGrowthDetail(slug, companyId, controller.signal)
      .then((d) => {
        setData(d);
        setLoaded(true);
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      });
    return () => controller.abort();
  }, [slug, companyId, revision, retry]);
  if (error)
    return (
      <p role="alert" className="text-sm text-slate-600">
        Calcul indisponible.
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setRetry((v) => v + 1)}
        >
          Réessayer
        </Button>
      </p>
    );
  if (!loaded)
    return (
      <Loader2
        aria-label="Chargement du score"
        className="h-4 w-4 animate-spin text-slate-400"
      />
    );
  return data?.assessment ? (
    <UsGrowthAssessment
      assessment={data.assessment}
      slug={slug}
      companyId={companyId}
      onSaved={setData}
    />
  ) : data?.score ? (
    <UsGrowthScoreCard score={data.score} heading={false} />
  ) : (
    <p className="text-sm text-slate-500">
      Score indisponible avec vos permissions actuelles.
    </p>
  );
}
